import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ArrowLeft, Wallet } from "lucide-react";
import { SafeText } from "@/shared/components/SafeText";
import { TestPartitionBadge } from "@/shared/components/TestPartitionBadge";
import { FoundingPartnerBadge } from "@/features/founding/FoundingPartnerBadge";
import { UserRoleBadge } from "../UserRoleBadge";
import { UserStatusBadge } from "../UserStatusBadge";
import type { UserDetail } from "../types";

interface UserDetailHeaderProps {
  readonly user: UserDetail;
  /** Whether the Founding Partner badge is rendered at all (viewer + target gate). */
  readonly showFounding: boolean;
  /** The founding-account read, `false` while loading or absent. */
  readonly foundingPartner: boolean;
  /** Whether the viewer may open the owner payout history (finance slice). */
  readonly canViewPayouts: boolean;
}

/** Back link, eyebrow, display name, badges and the payouts link of the user detail. */
export function UserDetailHeader({
  user,
  showFounding,
  foundingPartner,
  canViewPayouts,
}: UserDetailHeaderProps) {
  const { t } = useTranslation();
  return (
    <>
      <div>
        <Link
          to="/users"
          data-testid="user-detail-back"
          className="inline-flex items-center gap-1 font-sans text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          {t("users.detail.back")}
        </Link>
      </div>

      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-2">
          <p className="brand-eyebrow">{t("users.detail.eyebrow")}</p>
          <h1 className="text-3xl">
            <SafeText fallback={user.email}>{user.displayName}</SafeText>
          </h1>
          <div className="flex flex-wrap items-center gap-2">
            <UserRoleBadge role={user.role} />
            <UserStatusBadge isActive={user.isActive} />
            <TestPartitionBadge isTest={user.isTestAccount} testId="user-detail-test-badge" />
            {showFounding && (
              <FoundingPartnerBadge active={foundingPartner} testId="user-detail-founding-badge" />
            )}
          </div>
        </div>
        {canViewPayouts && (
          <Link
            to={`/finance/owners/${user.id}/payouts`}
            data-testid="view-owner-payouts"
            className="inline-flex items-center gap-2 rounded-md border border-input bg-transparent px-3 py-2 font-sans text-sm font-medium text-foreground shadow-sm hover:bg-muted"
          >
            <Wallet className="h-4 w-4" />
            {t("finance.ownerPayouts.viewLink")}
          </Link>
        )}
      </header>
    </>
  );
}
